import { Check, CheckCircle2, Clock, Copy, Download, Trash2, XCircle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';

import type { SessionDetailDto } from '../../schemas/sessionSchemas';
import { formatMobile } from '../../sessionFieldFormat';
import { toDisplayCase } from '../../sessionReference';
import { VerifiedStatusCell } from '../VerifiedStatusCell';

// The designer's SessionHeader: the run's steps as chips on the left, and the verdict
// dropdown plus the action buttons on the right. Values from SessionHeader.css —
// chip background #F4F6FD, header padding clamp(8px,0.7vw,12px), icon buttons
// clamp(28px,2.2vw,32px) square with a #666666 border, download green #00C232, delete in
// the #FEF2F2/#FCA5A5/#EF4444 danger trio.
//
// Two departures from the design, both because the mock's version cannot work here:
//
//   • The chips are NOT tabs. In the mock they switch which step is shown; our body lists
//     every step as an accordion, so clicking a chip SCROLLS to that step instead. A chip
//     that looked like a tab but only scrolled would be worse than one that plainly does.
//   • The mock's Share button is omitted. There is no session-level share flow to open,
//     and on desktop it would fall back to copying the link — which is the button beside
//     it. A second button doing the first button's job is not a feature.

interface ChipIcon {
  Icon: typeof CheckCircle2;
  className: string;
}

// Named separately so it is a value, not an index lookup — an unknown status falls back
// here rather than to `CHIP_ICON.Pending`, which the compiler cannot prove exists.
const PENDING_CHIP: ChipIcon = { Icon: Clock, className: 'text-[#6B7280]' };

const CHIP_ICON: Record<string, ChipIcon> = {
  Passed: { Icon: CheckCircle2, className: 'text-[#00C232]' },
  Failed: { Icon: XCircle, className: 'text-[#BF3636]' },
  Review: { Icon: Clock, className: 'text-[#F27310]' },
  Pending: PENDING_CHIP,
};

export function SessionDetailHeader({
  session,
  onDownloadPdf,
  onDelete,
  onFocusStep,
  activeStepId,
  stepNames,
}: {
  session: SessionDetailDto;
  onDownloadPdf: () => void;
  onDelete: () => void;
  /** Scrolls the body to a step's accordion. */
  onFocusStep: (stepId: string) => void;
  /**
   * The step currently at the top of the scrolled body, so its chip reads as the one you
   * are on. Without it the chips were write-only: they could move you to a step but never
   * told you which step you had arrived at, so after a scroll the row said nothing about
   * where you were.
   */
  activeStepId?: string;
  /**
   * Step id → a better label than `displayName`, for steps whose stored name is generic.
   *
   * Only questionnaire steps use it today: their `displayName` is frozen at run start, and one created
   * before the builder named these reads "Questionnaire". Resolved by the screen — see
   * useSessionQuestionnaireNames.
   */
  stepNames?: Map<string, string>;
}) {
  const [copied, setCopied] = useState(false);

  async function copyLink() {
    // The applicant's own page. Built from publicToken because that is the link that
    // actually opens a run; the session id would only be useful to someone with admin
    // access, who is already looking at this page.
    const link = `${window.location.origin}/w/${session.publicToken}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Applicant link copied');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access is refused on an insecure origin and by some browser policies.
      // Say so, rather than showing a success tick for something that did not happen.
      toast.error('Could not copy the link — copy it from the address bar instead.');
    }
  }

  return (
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-[#E4E4E4] bg-white p-[clamp(8px,0.7vw,12px)]">
      <div className="flex flex-wrap items-center gap-[clamp(6px,0.5vw,10px)]">
        {session.steps.map((step) => {
          const { Icon, className } = CHIP_ICON[step.status] ?? PENDING_CHIP;
          const active = step.id === activeStepId;
          return (
            <button
              key={step.id}
              type="button"
              onClick={() => onFocusStep(step.id)}
              title={`Go to ${stepNames?.get(step.id) ?? step.displayName} — ${step.status}`}
              // `aria-current` and not just colour: a screen reader gets the same "you are
              // here" that the fill gives everyone else.
              aria-current={active ? 'true' : undefined}
              className={cn(
                // `border` on BOTH states (transparent when inactive) so gaining the active
                // outline cannot nudge the row by a pixel and shuffle the chips sideways.
                'flex items-center gap-1.5 rounded-lg border px-[clamp(8px,0.7vw,12px)] py-[clamp(4px,0.4vw,6px)] text-[length:var(--text-14)] transition-all',
                // The designer's light-primary pair, taken from their Aadhaar Secure QR panel
                // (`bg-[#E5EDFF]` inside `border-[#99B6FF]`) with `#0048FF` for the text —
                // the same three values, used the same way.
                //
                // Their `.sc-session-nav-item--active` solid #0048FF fill was the first
                // choice, and it was too heavy here: that rule styles ONE selected row in a
                // vertical list, whereas this is a horizontal row of up to six chips, so a
                // saturated block reads as the loudest thing on the page rather than as a
                // position marker. The tint also lets the status icon keep its own colour,
                // which the white-on-blue version had to sacrifice.
                active
                  ? 'border-[#99B6FF] bg-[#E5EDFF] font-semibold text-[#0048FF]'
                  : 'border-transparent bg-[#F4F6FD] font-medium text-[#041A3A] hover:bg-[#E8EDFB]',
              )}
            >
              <span>{stepNames?.get(step.id) ?? step.displayName}</span>
              {/* Keeps its status colour in BOTH states. Green/orange/red all read cleanly on
                  #E5EDFF, so the active chip no longer has to trade its status away to show
                  that it is selected. */}
              <Icon size={13} className={cn('shrink-0', className)} />
            </button>
          );
        })}
      </div>

      <div className="ml-auto flex shrink-0 items-center gap-[clamp(6px,0.5vw,8px)]">
        {/* The same control as the sessions list, so a verdict is set the same way on
            both screens rather than through two implementations that can drift. The
            display status is preferred over the stored one because the rules about what
            may be set are written against the seven display values. */}
        <VerifiedStatusCell
          session={{
            id: session.id,
            originalStatus: session.originalStatus || session.status,
            verifiedStatus: session.verifiedStatus,
            label: formatMobile(session.applicantMobile) ?? undefined,
            // Case-normalised, matching every other place this name appears.
            applicantName: session.applicantFullName
              ? toDisplayCase(session.applicantFullName)
              : null,
            email: session.inviteEmail,
            // The detail's steps carry their own display names, so the resubmission dialog
            // names each check exactly as the rest of this screen does.
            steps: session.steps.map((s) => ({
              position: s.position,
              stepType: s.stepType,
              status: s.status,
              displayName: s.displayName,
            })),
          }}
        />

        <IconButton
          label="Download PDF"
          onClick={onDownloadPdf}
          className="border-[#00C232] bg-[#00C232] text-white shadow-xs hover:bg-[#009B28] hover:text-white"
        >
          <Download size={15} />
        </IconButton>

        <IconButton label="Copy applicant link" onClick={copyLink}>
          {copied ? <Check size={15} className="text-[#00C232]" /> : <Copy size={15} />}
        </IconButton>

        <IconButton
          label="Delete session"
          onClick={onDelete}
          className="border-[#FCA5A5] bg-[#FEF2F2] text-[#EF4444] hover:bg-[#FEE2E2] hover:text-[#DC2626]"
        >
          <Trash2 size={15} />
        </IconButton>
      </div>
    </div>
  );
}

/** `.sc-header-icon-btn` — a square bordered icon button. */
function IconButton({
  label,
  onClick,
  className,
  children,
}: {
  label: string;
  onClick: () => void;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={cn(
        'flex h-[clamp(28px,2.2vw,32px)] w-[clamp(28px,2.2vw,32px)] items-center justify-center rounded-md border border-[#666666] bg-white text-[#4B5563] transition-all hover:bg-[#F3F4F6] hover:text-[#111827]',
        className,
      )}
    >
      {children}
    </button>
  );
}
